import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAnalytics } from '../hooks/useAnalytics';
import { useStoryverseAnalytics } from '../hooks/useStoryverseAnalytics';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../contexts/APIContext';
import logger from '../utils/logger';
import { clampStoryResumeSeconds } from '../utils/progressStats';
import { updateStoryProgress } from '../utils/progressTracking';
import { supabase } from '../lib/supabaseClient';
import ShareVideoModal from './ShareVideoModal';
import FeedbackModal from './FeedbackModal';
import OriginalsOutroButtons from './OriginalsOutroButtons';
import SubtitleTracks from './SubtitleTracks';
import SubtitleControls from './SubtitleControls';
import SubtitleDebugOverlay from './SubtitleDebugOverlay';
import { useStorySubtitles } from '../hooks/useStorySubtitles';
import { useSubtitleControls, buildSubtitleMap } from '../hooks/useSubtitleControls';
import { subtitleDebug, logTextTracks } from '../utils/subtitleDebug';

// Session-level cache for feedback state (persists across component re-mounts)
let feedbackModalCache = {
  hasBeenShown: false,
  initialized: false
};

const VideoPlayer = ({
  isOpen,
  onClose,
  videoUrl,
  subtitleUrl = null,
  subtitleUrls: subtitleUrlsProp = null,
  title,
  description,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
  episodeId = null,
  storyId = null,
  showId = null,
  isStoryverse = false,
  isCommunityStory = false,
  onStoryverseViewIncrement = null,
  outro = null, // { seconds, buttonsAt } for Originals with a branded outro (src/config/originalsOutros.js)
  showName = null,
  nextEpisode = null, // { title, thumbnailUrl } for the Originals outro's "Next case" file
  currentThumbnailUrl = null, // this episode's thumbnail, for the outro's "Watch again" file
}) => {
  const { trackVideoWatched, trackStoryverseEpisodeWatched, trackVideoMinutesWatched } = useAnalytics();
  const { user, subscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const resolvedShowId = showId || storyId;
  const storyverseAnalyticsEnabled = isStoryverse && !!episodeId && !!resolvedShowId;
  const { onPlaybackStart: onStoryversePlaybackStart, onTimeUpdate: onStoryverseTimeUpdate, onVideoEnded: onStoryverseVideoEnded } = useStoryverseAnalytics({
    enabled: storyverseAnalyticsEnabled,
    isOpen,
    episodeId,
    showId: resolvedShowId,
    apiClient,
    userId: user?.id,
    onEpisodeStarted: onStoryverseViewIncrement,
  });
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const controlsTimeoutRef = useRef(null);
  const hasTrackedVideoStart = useRef(false);
  const lastTrackedTime = useRef(0);
  const totalWatchedSeconds = useRef(0);
  const hasShownCTA = useRef(false);
  const [showShareOverlay, setShowShareOverlay] = useState(false);
  const [outroEnded, setOutroEnded] = useState(false);
  const [videoFrame, setVideoFrame] = useState(null); // rendered picture rect inside the player (object-fit: contain)
  const [isShareVideoModalOpen, setIsShareVideoModalOpen] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showFeedbackNotification, setShowFeedbackNotification] = useState(false);
  const feedbackNotificationTimeoutRef = useRef(null);
  const sessionId = useRef(null);
  const lastProgressSyncAtRef = useRef(0);
  const hasFinishedProgressRef = useRef(false);
  const hasTrackedPlayCount = useRef(false);
  const contentId = storyId || episodeId;
  const { subtitleUrls: fetchedSubtitleUrls, requestSubtitle, loadingLang, apiStats, error: subtitleError } =
    useStorySubtitles(contentId);
  const mergedSubtitleUrls = React.useMemo(
    () => ({
      ...fetchedSubtitleUrls,
      ...buildSubtitleMap(subtitleUrl, subtitleUrlsProp),
    }),
    [fetchedSubtitleUrls, subtitleUrl, subtitleUrlsProp]
  );
  const subtitleControls = useSubtitleControls(videoRef, mergedSubtitleUrls, {
    enabled: isOpen,
    requestSubtitle,
    loadingLang,
  });
  const [textTrackCount, setTextTrackCount] = useState(0);
  const prevVideoUrlRef = useRef(videoUrl);

  useEffect(() => {
    subtitleDebug('VideoPlayer', 'props received', {
      contentId,
      storyId,
      episodeId,
      subtitleUrl,
      subtitleUrlsProp,
      mergedSubtitleUrls,
      isOpen,
    });
  }, [contentId, storyId, episodeId, subtitleUrl, subtitleUrlsProp, mergedSubtitleUrls, isOpen]);

  useEffect(() => {
    if (prevVideoUrlRef.current === videoUrl) return;
    subtitleDebug('VideoPlayer', 'videoUrl changed', {
      from: prevVideoUrlRef.current,
      to: videoUrl,
    });
    prevVideoUrlRef.current = videoUrl;
  }, [videoUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video?.textTracks || !isOpen) return undefined;

    const syncTrackCount = () => {
      setTextTrackCount(video.textTracks.length);
      logTextTracks(video, 'VideoPlayer addtrack');
    };

    video.textTracks.addEventListener('addtrack', syncTrackCount);
    syncTrackCount();

    return () => {
      video.textTracks.removeEventListener('addtrack', syncTrackCount);
    };
  }, [isOpen, mergedSubtitleUrls, subtitleControls.subtitleMap]);

  const incrementViewCount = async () => {
    const contentId = episodeId || storyId;
    if (!contentId) return;

    try {
      console.log(`🔍 incrementViewCount: contentId=${contentId}, episodeId=${episodeId}, storyId=${storyId}`);
      const { data: row, error: fetchErr } = await supabase
        .from('stories')
        .select('views, private_views')
        .eq('id', contentId)
        .maybeSingle();

      if (fetchErr) {
        console.error('❌ RLS or fetch error:', fetchErr);
        return;
      }

      if (!row) {
        console.warn(`⚠️ No story found for id ${contentId} — RLS may be blocking`);
        return;
      }

      const newViews = (Number(row.views) || 0) + 1;
      const newPrivateViews = (Number(row.private_views) || 0) + 1;

      const { error: updateErr } = await supabase
        .from('stories')
        .update({ views: newViews, private_views: newPrivateViews })
        .eq('id', contentId);

      if (updateErr) {
        console.error('❌ Update error:', updateErr);
        return;
      }

      console.log(`✅ Incremented story ${contentId}: views=${newViews}`);
    } catch (err) {
      console.error('❌ Error incrementing view count:', err);
    }
  };

  // Increment a counter on the stories table (share_count, download_count)
  const incrementStoryCounter = async (field) => {
    const contentId = episodeId || storyId;
    if (!contentId) return;

    try {
      const { data: row } = await supabase
        .from('stories')
        .select(field)
        .eq('id', contentId)
        .maybeSingle();

      if (row) {
        await supabase
          .from('stories')
          .update({ [field]: (Number(row[field]) || 0) + 1 })
          .eq('id', contentId);
      }
    } catch (err) {
      console.error(`Failed to increment ${field}:`, err);
    }
  };

  // Video event tracking function
  const logVideoEvent = async (eventType, videoElement) => {
    try {
      const userId = user?.id || 'guest_user';
      const videoId = episodeId || storyId || 'unknown';
      const videoPosition = Math.round(videoElement.currentTime);

      // Ensure we have a session ID
      if (!sessionId.current) {
        sessionId.current = uuidv4();
      }

      // Create new event object
      const newEvent = {
        type: eventType,
        position: videoPosition
      };

      // Fetch existing record for this user, video, and session
      const { data: existing, error: selectError } = await supabase
        .from('video_events')
        .select('id, data_tags')
        .eq('user_id', userId)
        .eq('video_id', videoId)
        .eq('session_id', sessionId.current)
        .maybeSingle();

      if (selectError) {
        console.error(`❌ Error fetching video_events:`, selectError);
        return;
      }

      // Append event to existing events array
      const updatedTags = {
        events: [...(existing?.data_tags?.events || []), newEvent]
      };

      if (existing?.id) {
        // Update existing record (timestamp updates automatically via default now())
        const { error: updateError } = await supabase
          .from('video_events')
          .update({
            data_tags: updatedTags
          })
          .eq('id', existing.id);

        if (updateError) {
          console.error(`❌ Error updating event:`, updateError);
        } else {
          console.log(`✅ Added ${eventType} event at ${videoPosition}s. Total events: ${updatedTags.events.length}`);
        }
      } else {
        // Insert new record (timestamp handled by default now())
        const { error: insertError } = await supabase
          .from('video_events')
          .insert({
            user_id: userId,
            video_id: videoId,
            session_id: sessionId.current,
            data_tags: updatedTags
          });

        if (insertError) {
          console.error(`❌ Error inserting event:`, insertError);
        } else {
          console.log(`✅ Added ${eventType} event at ${videoPosition}s. Total events: 1`);
        }
      }
    } catch (err) {
      console.error(`❌ Error logging ${eventType} event:`, err);
    }
  };

  // Initialize feedback cache from backend on user load
  useEffect(() => {
    if (user?.id && !feedbackModalCache.initialized) {
      const userDataTags = user.data_tags || [];
      // Check if any tag starts with FEEDBACK_MODAL_SHOWN (handles timestamps)
      const hasSeenInBackend = userDataTags.some(tag => tag.startsWith("FEEDBACK_MODAL_SHOWN"));
      
      feedbackModalCache.hasBeenShown = hasSeenInBackend;
      feedbackModalCache.initialized = true;
      
      logger.log(`📋 Feedback cache initialized from backend (users.data_tags):`, {
        hasBeenShown: feedbackModalCache.hasBeenShown,
        userId: user.id,
        dataTags: userDataTags
      });
    }
  }, [user]);

  //Safe version stops reload issue
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      hasTrackedVideoStart.current = false;
      lastTrackedTime.current = 0;
      totalWatchedSeconds.current = 0;
      hasShownCTA.current = false;
      setShowShareOverlay(false);
      // Reset / create new session only when video opens
      sessionId.current = uuidv4();
      lastProgressSyncAtRef.current = 0;
      hasFinishedProgressRef.current = false;
      hasTrackedPlayCount.current = false;
      console.log(`🎬 New session started: ${sessionId.current}`);
    }
  }, [isOpen]);

  const syncStoryProgress = (status = 'IN_PROGRESS') => {
    if (!videoRef.current || !storyId || !user?.id || !apiClient) return;
    if (hasFinishedProgressRef.current && status !== 'FINISHED') return;

    const now = Date.now();
    const duration = Number(videoRef.current.duration || 0);
    const current = Number(videoRef.current.currentTime || 0);
    const progress =
      status === 'FINISHED'
        ? Math.max(duration, current)
        : clampStoryResumeSeconds(current, duration);

    if (status === 'IN_PROGRESS') {
      if (progress <= 0) return;
      if (videoRef.current.paused) return;
      if (now - lastProgressSyncAtRef.current < 5000) return;
    }

    if (status === 'FINISHED') {
      hasFinishedProgressRef.current = true;
    }
    lastProgressSyncAtRef.current = now;
    updateStoryProgress(apiClient, user.id, storyId, progress, status);
  };

  // Reset tracking refs when video URL changes (e.g., next/previous episode)
  useEffect(() => {
    setShowShareOverlay(false);
    setOutroEnded(false);
    hasShownCTA.current = false;
    hasTrackedPlayCount.current = false;
  }, [videoUrl]);

  // Track where the picture actually sits (letterboxing differs by screen shape) so the Originals
  // outro buttons can land in the outro's empty zone at any size.
  useEffect(() => {
    if (!outro || !isOpen) return undefined;
    const v = videoRef.current;
    if (!v) return undefined;
    const measure = () => {
      const vw = v.videoWidth, vh = v.videoHeight, ew = v.clientWidth, eh = v.clientHeight;
      if (!vw || !vh || !ew || !eh) return;
      const k = Math.min(ew / vw, eh / vh);
      const w = vw * k, h = vh * k;
      setVideoFrame({ left: v.offsetLeft + (ew - w) / 2, top: v.offsetTop + (eh - h) / 2, width: w, height: h });
    };
    measure();
    v.addEventListener('loadedmetadata', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(v);
    window.addEventListener('resize', measure);
    return () => {
      v.removeEventListener('loadedmetadata', measure);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [outro, isOpen, videoUrl]);

  // Reset video position only when URL changes (not mid-play)
  useEffect(() => {
    if (videoRef.current) {
      setIsLoading(true);
      videoRef.current.currentTime = 0;
    }
  }, [videoUrl]);

  // Auto-play video when player opens (if autoplay flag is set)
  useEffect(() => {
    if (!isOpen || !videoUrl) return;

    const video = videoRef.current;
    if (!video) return;

    const shouldAutoPlay = sessionStorage.getItem("autoplay_video_player") === "true";
    if (!shouldAutoPlay) return;

    // Remove flag immediately to prevent re-triggering
    sessionStorage.removeItem("autoplay_video_player");

    const playVideo = async () => {
      try {
        await video.play();
        logger.log("✅ Video auto-played successfully in VideoPlayer");
      } catch (err) {
        // Autoplay was prevented - this is expected in some browsers
        logger.log("ℹ️ Autoplay blocked by browser (user interaction required)");
        // Video will remain paused, user can click play button
      }
    };

    // Check if video is already ready (readyState 2 = HAVE_CURRENT_DATA or higher)
    if (video.readyState >= 2) {
      playVideo();
    } else {
      // Wait for video to be ready
      video.addEventListener("canplay", playVideo, { once: true });
    }

    return () => {
      video.removeEventListener("canplay", playVideo);
    };
  }, [isOpen, videoUrl]); // Re-run when player opens or video URL changes

  // Track video start and watch time
  // Add WATCH tag as soon as video player opens (not gated on isPlaying)
  const hasTrackedWatchTag = useRef(false);
  useEffect(() => {
    if (!isOpen || hasTrackedWatchTag.current) return;
    const contentId = episodeId || storyId;
    if (contentId && apiClient && user?.id) {
      hasTrackedWatchTag.current = true;
      const tag = `WATCH_${contentId}`;
      console.log(`🏷️ Adding WATCH tag: ${tag} for user ${user.id}`);
      apiClient.addUserDataTag(user.id, tag).then((result) => {
        console.log(`🏷️ WATCH tag result:`, result);
      }).catch((err) => {
        console.error(`🏷️ WATCH tag error:`, err);
      });
    }
  }, [isOpen, episodeId, storyId, apiClient, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset watch tag ref when video changes
  useEffect(() => {
    if (isOpen) {
      hasTrackedWatchTag.current = false;
    }
  }, [isOpen, videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !isPlaying) return;

    // Track video start (only once per video)
    if (!hasTrackedVideoStart.current && user?.id) {
      // Track general video watched event
      trackVideoWatched({
        title,
        episodeId,
        storyId,
        isStoryverse,
        subscriptionType: subscriptionStatus.subscriptionType,
      });

      // Track specific Storyverse episode watched event if it's a Storyverse video
      if (isStoryverse) {
        trackStoryverseEpisodeWatched({
          title,
          episodeId,
          subscriptionType: subscriptionStatus.subscriptionType,
        });
      }

      hasTrackedVideoStart.current = true;
    }

    // Track watch time every 10 seconds
    const interval = setInterval(() => {
      if (videoRef.current && !videoRef.current.paused) {
        const currentVideoTime = videoRef.current.currentTime;
        const timeDiff = currentVideoTime - lastTrackedTime.current;

        // Only track if time difference is reasonable (to avoid seeking)
        if (timeDiff > 0 && timeDiff <= 15) {
          totalWatchedSeconds.current += timeDiff;

          // Track minutes watched (report every time we accumulate a minute)
          if (totalWatchedSeconds.current >= 60) {
            const minutesWatched = Math.floor(totalWatchedSeconds.current / 60);

            if (user?.id) {
              trackVideoMinutesWatched({
                title,
                episodeId,
                storyId,
                minutesWatched,
                subscriptionType: subscriptionStatus.subscriptionType,
              });
            }

            // Reset the counter after reporting
            totalWatchedSeconds.current = totalWatchedSeconds.current % 60;
          }
        }

        lastTrackedTime.current = currentVideoTime;
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(interval);
  }, [isOpen, isPlaying, user, title, episodeId, storyId, subscriptionStatus.subscriptionType, apiClient, isStoryverse]);

  // Listen for custom event to open feedback modal from notification
  useEffect(() => {
    const handleOpenFeedbackModal = () => {
      setShowFeedbackNotification(false);
      setShowFeedbackModal(true);
    };

    window.addEventListener("openFeedbackModal", handleOpenFeedbackModal);
    return () => {
      window.removeEventListener("openFeedbackModal", handleOpenFeedbackModal);
    };
  }, []);

  // Cleanup notification timeout on unmount
  useEffect(() => {
    return () => {
      if (feedbackNotificationTimeoutRef.current) {
        clearTimeout(feedbackNotificationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;
      
      // Ignore keyboard shortcuts when FeedbackModal is open
      if (showFeedbackModal || showFeedbackNotification) return;
      
      // Ignore keyboard shortcuts when user is typing in input fields
      const target = e.target;
      const isInputField = 
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable;
      
      if (isInputField) return;
      
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlayPause();
          break;
        case 'Escape':
          onClose();
          break;
        case 'f':
          toggleFullscreen();
          break;
        case 'm':
          toggleMute();
          break;
        case 'ArrowLeft':
          skipTime(-10);
          break;
        case 'ArrowRight':
          skipTime(10);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, isPlaying, onClose, showFeedbackModal, showFeedbackNotification]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const skipTime = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleProgressClick = (e) => {
    if (progressRef.current && videoRef.current) {
      const rect = progressRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      videoRef.current.currentTime = newTime;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setIsMuted(newVolume === 0);
    }
  };

  const formatTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVideoEnd = () => {
    // Pause the video when it ends
    if (videoRef.current) {
      videoRef.current.pause();
    }
    setShowControls(true);

    // Originals with a branded outro end on their own end card: the outro buttons replace the
    // share overlay / feedback prompts, which would otherwise cover them.
    if (outro) {
      setOutroEnded(true);
      return;
    }
    
    // Check if user should see feedback modal - ONCE PER USER LIFETIME
    if (user?.id) {
      logger.log(`🎬 Video ended. Checking feedback eligibility:`, {
        userId: user.id,
        cacheHasBeenShown: feedbackModalCache.hasBeenShown,
        cacheInitialized: feedbackModalCache.initialized
      });

      // Check session cache FIRST (prevents multiple shows in same session)
      if (feedbackModalCache.hasBeenShown) {
        logger.log("🔔 Returning user: showing share overlay + notification (2s delay)");
        
        // Show share overlay immediately
        setShowShareOverlay(true);
        
        // Clear any existing notification timeout
        if (feedbackNotificationTimeoutRef.current) {
          clearTimeout(feedbackNotificationTimeoutRef.current);
        }
        
        // Show small notification after 2 seconds (if full modal not open)
        feedbackNotificationTimeoutRef.current = setTimeout(() => {
          if (!showFeedbackModal) {
            setShowFeedbackNotification(true);
            logger.log("📬 Small feedback notification shown");
          }
          feedbackNotificationTimeoutRef.current = null;
        }, 2000);
        
        return;
      }

      // Double-check backend as final safety (in case cache wasn't initialized)
      const userDataTags = user.data_tags || [];
      // Check if any tag starts with FEEDBACK_MODAL_SHOWN (handles timestamps)
      const hasSeenInBackend = userDataTags.some(tag => tag.startsWith("FEEDBACK_MODAL_SHOWN"));
      
      if (hasSeenInBackend) {
        logger.log("🔔 Returning user (backend check): showing share overlay + notification (2s delay)");
        feedbackModalCache.hasBeenShown = true; // Update cache
        
        // Show share overlay immediately
        setShowShareOverlay(true);
        
        // Clear any existing notification timeout
        if (feedbackNotificationTimeoutRef.current) {
          clearTimeout(feedbackNotificationTimeoutRef.current);
        }
        
        // Show small notification after 2 seconds (if full modal not open)
        feedbackNotificationTimeoutRef.current = setTimeout(() => {
          if (!showFeedbackModal) {
            setShowFeedbackNotification(true);
            logger.log("📬 Small feedback notification shown");
          }
          feedbackNotificationTimeoutRef.current = null;
        }, 2000);
        
        return;
      }

      // First-time user: show full feedback modal
      logger.log("🆕 First-time user: showing full feedback modal (ONCE PER LIFETIME)");
      
      // IMMEDIATELY mark as shown in cache (prevents duplicate triggers)
      feedbackModalCache.hasBeenShown = true;
      
      // Show modal
      setShowFeedbackModal(true);
      
      // Persist to backend (async, for future sessions/devices)
      logger.log("💾 Adding FEEDBACK_MODAL_SHOWN to users.data_tags...");
      apiClient.addUserDataTag(user.id, "FEEDBACK_MODAL_SHOWN")
        .then((result) => {
          if (result.status) {
            logger.log("✅ FEEDBACK_MODAL_SHOWN successfully added to users.data_tags");
          } else {
            logger.warn("⚠️ Failed to add to users.data_tags:", result.error);
          }
        })
        .catch((error) => {
          logger.error("❌ Error adding to users.data_tags:", error);
        });
      // Share overlay will be shown when feedback modal closes (in onClose handler)
    } else {
      // Guest users: show share overlay immediately (no feedback flow)
      logger.log("👤 Guest user: showing share overlay only");
      setShowShareOverlay(true);
    }
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
      {/* Video Container */}
      <div
        className="relative w-full h-full flex items-center justify-center"
        onMouseMove={showControlsTemporarily}
        onMouseEnter={() => setShowControls(true)}
      >
        <SubtitleDebugOverlay
          contentId={contentId}
          subtitleUrl={subtitleUrl}
          subtitleUrlsProp={subtitleUrlsProp}
          fetchedSubtitleUrls={fetchedSubtitleUrls}
          mergedSubtitleUrls={mergedSubtitleUrls}
          selectedLang={subtitleControls.selectedLang}
          subtitlesEnabled={subtitleControls.subtitlesEnabled}
          loadingLang={loadingLang}
          subtitleError={subtitleError}
          textTrackCount={textTrackCount}
          apiStats={apiStats}
        />

        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-red-600"></div>
          </div>
        )}

        {/* Video Element */}
        <video
          ref={videoRef}
          src={videoUrl}
          className="pixley-modal-video"
          preload="auto"
          playsInline
          controlsList="nodownload"
          disablePictureInPicture={false}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            width: '100%',
            height: '100%',
            maxWidth: '100vw',
            maxHeight: '100vh',
            objectFit: 'contain'
          }}
          onPlay={(e) => {
            setIsPlaying(true);
            logVideoEvent('play', e.target);
            if (storyverseAnalyticsEnabled) {
              onStoryversePlaybackStart(e.target);
            }
            // Increment view counts and play_count (non-blocking, once per session)
            if (!hasTrackedPlayCount.current) {
              hasTrackedPlayCount.current = true;
              setTimeout(() => {
                incrementViewCount().catch(err => {
                  console.error('❌ Error in incrementViewCount:', err);
                });
              }, 0);
            }
          }}
          onPause={(e) => {
            setIsPlaying(false);
            logVideoEvent('pause', e.target);
          }}
          onSeeked={(e) => {
            logVideoEvent('seek', e.target);
          }}
          onTimeUpdate={() => {
            setCurrentTime(videoRef.current?.currentTime || 0);
            syncStoryProgress('IN_PROGRESS');
            if (storyverseAnalyticsEnabled && videoRef.current) {
              onStoryverseTimeUpdate(videoRef.current);
            }
          }}
          onLoadedMetadata={() => {
            setDuration(videoRef.current?.duration || 0);
            setIsLoading(false);
            const count = videoRef.current?.textTracks?.length ?? 0;
            setTextTrackCount(count);
            subtitleDebug('VideoPlayer', 'onLoadedMetadata — textTracks snapshot', {
              count,
              videoUrl,
              selectedLang: subtitleControls.selectedLang,
              subtitlesEnabled: subtitleControls.subtitlesEnabled,
            });
            logTextTracks(videoRef.current, 'VideoPlayer onLoadedMetadata');
          }}
          onWaiting={() => setIsLoading(true)}
          onCanPlay={() => setIsLoading(false)}
          onEnded={(e) => {
            logVideoEvent('ended', e.target);
            syncStoryProgress('FINISHED');
            if (storyverseAnalyticsEnabled) {
              onStoryverseVideoEnded(e.target);
            }
            handleVideoEnd();
          }}
          onClick={togglePlayPause}
          crossOrigin="anonymous"
          autoPlay
        >
          <SubtitleTracks subtitleMap={subtitleControls.subtitleMap} />
        </video>

        {/* Share Overlay - Shows after video ends (only if feedback modals are not showing) */}
        {showShareOverlay && (
          <div
            className="absolute inset-0 flex items-center justify-center z-50 p-4"
            style={{ background: "rgba(11,26,51,0.55)", backdropFilter: "blur(8px)", fontFamily: "'Inter', 'Fredoka', sans-serif" }}
          >
            <div
              className="relative w-full max-w-md overflow-hidden p-7"
              style={{
                background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
                border: "2px solid rgba(11,26,51,0.06)",
                borderRadius: 24,
                boxShadow: "0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)",
              }}
            >
              {/* Decorative blob */}
              <div aria-hidden="true" style={{ position: "absolute", top: -70, right: -70, width: 200, height: 200, borderRadius: "50%", background: "rgba(0,101,248,0.10)", filter: "blur(20px)", pointerEvents: "none" }} />

              <div className="relative text-center mb-5">
                <span style={{ fontFamily: "'Fredoka', sans-serif", color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                  Share
                </span>
                <h3 style={{ fontFamily: "'Fredoka', sans-serif", color: "#0B1A33", fontWeight: 700, fontSize: "clamp(1.4rem, 2.6vw, 1.8rem)", textTransform: "uppercase", letterSpacing: "-0.01em", lineHeight: 1.1, margin: "4px 0 6px" }}>
                  Share this story
                </h3>
                <p style={{ fontFamily: "'Fredoka', sans-serif", color: "#5A6B85", fontSize: 14, margin: 0 }}>
                  Let friends and family enjoy this story too.
                </p>
              </div>

              <div className="relative flex flex-col items-center gap-2.5">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    logger.log("Share button clicked from overlay");
                    setIsShareVideoModalOpen(true);
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 transition active:scale-[0.97]"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 15,
                    border: "none",
                    borderRadius: 999,
                    padding: "13px 22px",
                    boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                    cursor: "pointer",
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Share to Social
                </button>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowShareOverlay(false);
                  }}
                  className="transition active:scale-[0.97]"
                  style={{
                    fontFamily: "'Fredoka', sans-serif",
                    background: "transparent",
                    color: "#94A3B8",
                    fontWeight: 700,
                    fontSize: 13,
                    border: "none",
                    padding: "8px 18px",
                    cursor: "pointer",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#5A6B85")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#94A3B8")}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Originals outro end-card buttons (pop into the space the outro leaves empty) */}
        {outro && (
          <OriginalsOutroButtons
            visible={outroEnded || (duration > 0 && currentTime >= duration - outro.seconds + (outro.buttonsAt || 0))}
            ended={outroEnded}
            hasNext={hasNext}
            onNext={() => { setOutroEnded(false); onNext && onNext(); }}
            onReplay={() => {
              setOutroEnded(false);
              if (videoRef.current) { videoRef.current.currentTime = 0; videoRef.current.play().catch(() => {}); }
            }}
            episodeId={episodeId}
            showId={resolvedShowId}
            showName={showName}
            frame={videoFrame}
            nextEpisode={nextEpisode}
            currentThumbnailUrl={currentThumbnailUrl}
          />
        )}

        {/* Controls Overlay */}
        <div 
          className={`absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 transition-opacity duration-300 ${
            showControls ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {/* Top Bar */}
          <div className="absolute top-0 left-0 right-0 p-3 sm:p-6 flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  logger.log('X button clicked'); // Debug log
                  onClose();
                }}
                className="text-white hover:text-red-500 transition-colors duration-200 relative z-10 flex-shrink-0"
              >
                <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="min-w-0">
                <h2 className="text-white text-sm sm:text-xl font-semibold truncate">{title}</h2>
                {description && (
                  <p className="text-gray-300 text-xs sm:text-sm mt-0.5 sm:mt-1 max-w-md truncate hidden sm:block">{description}</p>
                )}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleFullscreen();
              }}
              className="text-white hover:text-red-500 transition-colors duration-200 relative z-10 cursor-pointer"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
              </svg>
            </button>
          </div>

          {/* Center Play Button (when paused) */}
          {!isPlaying && !isLoading && !outroEnded && (
            <div className="absolute inset-0 flex items-center justify-center">
              <button
                onClick={togglePlayPause}
                className="w-14 h-14 sm:w-20 sm:h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-200 hover:scale-110"
              >
                <svg className="w-7 h-7 sm:w-10 sm:h-10 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </div>
          )}

          {/* Bottom Controls */}
          <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-6 space-y-2 sm:space-y-4">
            {/* Progress Bar */}
            <div className="space-y-1 sm:space-y-2">
              <div 
                ref={progressRef}
                className="w-full h-1.5 sm:h-2 bg-white/20 rounded-full cursor-pointer hover:h-3 transition-all duration-200"
                onClick={handleProgressClick}
              >
                <div 
                  className="h-full bg-red-600 rounded-full relative"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                >
                  <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-3 h-3 sm:w-4 sm:h-4 bg-red-600 rounded-full opacity-0 hover:opacity-100 transition-opacity duration-200"></div>
                </div>
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-gray-300">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-4">
                {/* Play/Pause */}
                <button
                  onClick={togglePlayPause}
                  className="text-white hover:text-red-500 transition-colors duration-200"
                >
                  {isPlaying ? (
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                    </svg>
                  ) : (
                    <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  )}
                </button>

                {/* Skip Backward */}
                <button
                  onClick={() => skipTime(-10)}
                  className="text-white hover:text-red-500 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0019 16V8a1 1 0 00-1.6-.8l-5.334 4zM4.066 11.2a1 1 0 000 1.6l5.334 4A1 1 0 0011 16V8a1 1 0 00-1.6-.8l-5.334 4z" />
                  </svg>
                </button>

                {/* Skip Forward */}
                <button
                  onClick={() => skipTime(10)}
                  className="text-white hover:text-red-500 transition-colors duration-200"
                >
                  <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
                  </svg>
                </button>

                {/* CC Button + Language Dropdown — always available when content id exists */}
                {contentId && (
                  <SubtitleControls {...subtitleControls} />
                )}

                {/* Volume Control - hidden on mobile (use device volume) */}
                <div className="hidden sm:flex items-center space-x-2 group">
                  <button
                    onClick={toggleMute}
                    className="text-white hover:text-red-500 transition-colors duration-200"
                  >
                    {isMuted || volume === 0 ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      </svg>
                    )}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={isMuted ? 0 : volume}
                    onChange={handleVolumeChange}
                    className="w-20 h-1 bg-white/20 rounded-lg appearance-none cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    style={{
                      background: `linear-gradient(to right, #dc2626 0%, #dc2626 ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.2) 100%)`
                    }}
                  />
                </div>
              </div>

              {/* Episode Navigation */}
              <div className="flex items-center space-x-1.5 sm:space-x-2">
                {hasPrevious && (
                  <button
                    onClick={onPrevious}
                    className="px-2 py-1.5 sm:px-4 sm:py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 border border-white/20"
                  >
                    <span className="hidden sm:inline">Previous Episode</span>
                    <span className="sm:hidden">Prev</span>
                  </button>
                )}
                {hasNext && (
                  <button
                    onClick={onNext}
                    className="px-2 py-1.5 sm:px-4 sm:py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs sm:text-sm font-medium transition-all duration-200"
                  >
                    <span className="hidden sm:inline">Next Episode</span>
                    <span className="sm:hidden">Next</span>
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Share Video Modal */}
      {isShareVideoModalOpen && videoUrl && (
        <ShareVideoModal
          isOpen={isShareVideoModalOpen}
          onClose={() => {
            setIsShareVideoModalOpen(false);
            setShowShareOverlay(false);
          }}
          videoUrl={videoUrl}
          storyTitle={title}
          videoRef={videoRef}
          user={user}
          subscriptionStatus={subscriptionStatus}
          apiClient={apiClient}
          storyId={storyId}
          onShare={() => incrementStoryCounter('share_count')}
          onDownload={() => incrementStoryCounter('download_count')}
        />
      )}

      {/* Feedback Modal - Full screen for first-time users */}
      {user?.id && (
        <FeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => {
            setShowFeedbackModal(false);
            // Share overlay is already shown - no need to re-open it
          }}
          variant="full"
          storyId={storyId}
          contentType="video"
        />
      )}

      {/* Feedback Notification - Small notification for returning users */}
      {user?.id && (
        <FeedbackModal
          isOpen={showFeedbackNotification}
          onClose={() => setShowFeedbackNotification(false)}
          onOpenFullModal={() => {
            setShowFeedbackNotification(false);
            setShowFeedbackModal(true);
          }}
          variant="notification"
          storyId={storyId}
          contentType="video"
        />
      )}
    </div>
  );
};

export default VideoPlayer;